<template>
    <div class="dropdown">
        <span class="btn dropdown-toggle" role="button" @click.self.stop="toggleDropdown"> </span>
        <div class="dropdown-menu dropdown-menu-right" v-if="showDropdown">
            <template v-if="isSquareBrick">
                <a v-if="displayInfo"
                   
                   :class="[canClickInfo ? '' : 'disabled']" 
                   class="dropdown-item" 
                   href="#"
                   @click="infoClick"
                >
                    <i class="fas fa-info-circle"/> {{resource('JourneyBuilder_JourneyBrickMenu_Info_DisplayName')}}
                </a>

                <navigate-to-actionable-report-link v-if="displayNavigateToActionableReportLink"
                    :journey-id="currentJourneyId"
                    :highlighted-brick-id="brickId"
    
                    class="dropdown-item"
                    :disabled="!canNavigateToActionableReport"
                />

                <a :class="[canClickAddConnection ? '' : 'disabled']" class="dropdown-item" href="#" @click="addConnectionClick">
                    <i class="fas fa-bullseye"/> {{resource('JourneyBuilder_JourneyBrickMenu_AddRoute_DisplayName')}}
                </a>

                <a class="dropdown-item" href="#" @click="settingsClick">
                    <i class="fas fa-cog"/>{{resource('JourneyBuilder_JourneyBrickMenu_Settings_DisplayName')}}
                </a>

                <a :class="[canClickDelete ? '' : 'disabled']" class="dropdown-item" href="#" @click="deleteClick">
                    <i class="fas fa-trash"/>{{resource('JourneyBuilder_JourneyBrickMenu_Remove_DisplayName')}}
                </a>

                <template v-if="isJourneyBrick">
                    <a :class="[journeyUrl ? '' : 'disabled']"  class="dropdown-item" :href="journeyUrl || '#'" @click="showDropdown = false">
                        <i class="fas fa-route"></i>{{resource('JourneyBuilder_JourneyBrickMenu_NavigateToJourney_DisplayName')}}
                    </a>
                </template>
            </template>
            
            <template v-if="supportsComments">
                <a :class="{ 'open': showDropdown && showCommentSection, 'disabled': !hasComments }" 
                   class="dropdown-item" 
                   href="#" 
                   @click.prevent.stop="toggleCommentSection"
                >
                    <span class="fa-stack">
                        <i class="far fa-comment-alt fa-stack-2x" />
                        <i class="fas fa-align-left fa-stack-1x" />
                    </span>
                    {{resource('JourneyBuilder_JourneyBrickMenu_Comments_DisplayName')}}
                    
                    <has-unseen-comments-marker class="has-unseen-comments-marker" v-if="hasUnseenComments" />
                </a>
            </template>
        </div>
        
        <comments-section
            v-if="showDropdown && showCommentSection"
            class="menu-comments-section"
            :related-id="brickId" 
            type="Brick"
            
            @click.native.stop
            @wheel.native.stop
        />
    </div>
</template>

<script>
    /*
        Popper.js attempts to position our dropdown via the CSS transform(...) API
        this does not play well with our zooming framework
        
        since we only use the show/hide feature (no positioning), we should handle the popup ourselves
     */

    import {createNamespacedHelpers} from 'vuex';
    
    import brickTypes from '@modules/JourneyBuilder/Bricks/brick-types';
    import {brickDefinitions} from "@modules/JourneyBuilder/Bricks/brick-definitions";
    import api from "@modules/JourneyBuilder/api";
    
    import resources from '@core/Util/Mixins/resources';
    import permissionsMixin from '@modules/JourneyBuilder/Mixins/journey-permissions';
    
    import { 
        CommentsSection,
        HasUnseenCommentsMarker
    } from "./Comments";
    import { NavigateToActionableReportLink } from "@modules/JourneyBuilder/Components/Elements";

    const {mapState, mapGetters, mapMutations} = createNamespacedHelpers('journeyBuilder/composer');

    export default {
        name: 'brick-dropdown-menu',
        mixins: [resources, permissionsMixin],
        components: {
            CommentsSection,
            HasUnseenCommentsMarker,
            NavigateToActionableReportLink
        },
        methods: {
            ...mapMutations(['toggleMenuShown']),
            infoClick(){
                if(this.canClickInfo){
                    this.emitClick('info', arguments);   
                }
            },
            addConnectionClick(){
                if(this.canClickAddConnection){
                    this.emitClick('add-connection', arguments);
                }
            },
            settingsClick(){
                this.emitClick('settings', arguments);
            },
            deleteClick(){
                if(this.canClickDelete){
                    this.emitClick('delete', arguments);
                }
            },
            emitClick(type, args){
                this.showDropdown = false;
                this.$emit(`${type}-click`, ...args);
            },
            toggleCommentSection(){
                if(this.hasComments){
                    this.showCommentSection = !this.showCommentSection;   
                }
            },
            toggleDropdown(){
                this.showDropdown = !this.showDropdown;
            }
        },
        watch: {
            showDropdown(value){
                const vm = this;
                
                /*
                    We want to close our dropdown when shown, but a click takes place outside of our element
                    thus we attach a global click handler on show
                    as to prevent a memory leak/bogging down; we need to remove it the moment our dropdown is hidden again
                 */
                
                if(value){
                    const handlerRef = () => vm.showDropdown = false;
                    document.addEventListener('click', handlerRef);
                    
                    vm.globalClickHandlerRef = handlerRef;

                //     vm.$emit('open');
                    this.toggleMenuShown({brickId: this.brickId, shown: true});
                }
                else {
                    //Comment section should always be disabled on close
                    this.showCommentSection = false;
                    
                    const handlerRef = vm.globalClickHandlerRef;
                    if(handlerRef){
                        document.removeEventListener('click', handlerRef);
                    }
                    
                    // vm.$emit('close');
                    this.toggleMenuShown({brickId: this.brickId, shown: false});
                }
            }
        },
        data: () => ({
            id: '',
            showDropdown: false,
            showCommentSection: false,
            
            globalClickHandlerRef: null
        }),
        computed: {
            ...mapState({
                'brickMap': 'brickMap',
                'currentJourneyId': 'journeyId'
            }),
            ...mapGetters({
                'storeHasUnseenComments': 'hasUnseenComments',
                'storeHasComments': 'hasComments'
            }),
            brickType(){
                return this.brickMap[this.brickId].type;
            },
            canClickInfo(){
                return !this.isDisabled &&
                    !this.brickMap[this.brickId].isNew;
            },
            canClickAddConnection(){
                if (
                    //Audience filter connections are added based on brick configuration
                    //audience filter bricks should therefor not be able to manually add connections
                    this.brickType === brickTypes.audienceFilter ||
                    
                    //Goal bricks have a single outgoing connection - this connection is defined within the goal brick itself
                    //users are not allowed to add any more outgoing connections
                    this.brickType === brickTypes.goal
                ) {
                    return false;
                }
                
               return !this.isDisabled && this.canWrite;
            },
            canClickDelete(){
                return this.canWrite && !this.isChildJourneyBrick;
            },
            supportsComments(){
                return brickDefinitions[this.brickType].supportsComments;
            },
            hasUnseenComments(){
                return this.storeHasUnseenComments(this.brickId);
            },
            hasComments(){
                return this.storeHasComments(this.brickId);
            },
            isJourneyBrick(){
                return this.brickType === brickTypes.journey;
            },
            journeyUrl(){
                if(!this.isJourneyBrick || !this.configuredJourneyId){
                    return null;
                }
                
                return api.getViewJourneyUrl(this.configuredJourneyId);
            },
            displayInfo(){
                return brickTypes.isTouchpointLike(this.brickMap[this.brickId].type);
            },
            displayNavigateToActionableReportLink(){
                return brickTypes.isTouchpointLike(this.brickMap[this.brickId].type)
            },
            canNavigateToActionableReport(){
                //Journey must not be new
                //Brick must not be new
                return this.currentJourneyId && !this.brickMap[this.brickId].isNew;
            }
        },
        props: {
            brickId: {
                type: String,
                required: true
            },
            //isDisabled currently isn't used, leaving it in here won't cause any harm however
            isDisabled: {
                type: Boolean,
                default: false,
                required: false
            },
            hasConnections: {
                type: Boolean,
                required: false,
                default: false
            },
            isChildJourneyBrick: {
                type: Boolean,
                required: false,
                default: false
            },
            //Bit hacky; this menu was originally inteded for just square bricks
            //other bricks only get the comments option
            isSquareBrick: {
                type: Boolean,
                required: false,
                default: false
            },
            configuredJourneyId: {
                type: String,
                required: false,
                default: null
            }
        }
    }
</script>

<style scoped lang="scss">
    @import "~@modules/JourneyBuilder/Style/journey-brick-menu.scss";
</style>